import { Injectable, NgZone } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Subscription, fromEvent } from "rxjs";
import { filter } from "rxjs/operators";

import { CommandPaletteDialogComponent } from "src/app/modules/shared/components/command-palette/command-palette-dialog.component";

/**
 * Command Palette (Ctrl/Cmd+K). Punto de entrada único:
 *  - `open()` monta `CommandPaletteDialogComponent` en un MatDialog con panelClass
 *    y backdropClass propios (estética y blur a medida).
 *  - El atajo ⌘K / Ctrl+K se captura GLOBALMENTE aquí, fuera de la zona de Angular.
 */
@Injectable({ providedIn: "root" })
export class CommandPaletteService {
	private ref: MatDialogRef<CommandPaletteDialogComponent> | null = null;
	private shortcutSub: Subscription | null = null;

	constructor(
		private readonly dialog: MatDialog,
		private readonly zone: NgZone,
	) {
		this.bindGlobalShortcut();
	}

	open(): void {
		if (this.ref) {
			return;
		}
		this.ref = this.dialog.open(CommandPaletteDialogComponent, {
			panelClass: "cmd-palette-panel",
			backdropClass: "cmd-palette-backdrop",
			autoFocus: "first-tabbable",
			restoreFocus: true,
			hasBackdrop: true,
			disableClose: false,
			ariaModal: true,
		});
		this.ref.afterClosed().subscribe(() => {
			this.ref = null;
		});
	}

	close(): void {
		this.ref?.close();
		this.ref = null;
	}

	get isOpen(): boolean {
		return this.ref !== null;
	}

	private bindGlobalShortcut(): void {
		this.zone.runOutsideAngular(() => {
			this.shortcutSub = fromEvent<KeyboardEvent>(window, "keydown")
				.pipe(filter((ev) => this.isPaletteShortcut(ev)))
				.subscribe((ev) => {
					ev.preventDefault();
					if (this.ref) {
						return;
					}
					this.zone.run(() => this.open());
				});
		});
	}

	private isPaletteShortcut(ev: KeyboardEvent): boolean {
		if (ev.key !== "k" && ev.key !== "K") {
			return false;
		}
		// Cmd en macOS / Ctrl en el resto; aceptamos ambos sin importar la plataforma.
		return ev.metaKey || ev.ctrlKey;
	}
}
